import React, { useRef, useEffect, useState } from "react";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    ControlledMenu, MenuHeader, MenuItem, SubMenu, useMenuState,
} from "@szhsin/react-menu";
import { styled } from "@mui/material/styles";
import { Badge } from "@mui/material";
import useStore from "../../../store";
import { PTMTooltip } from "../../../Components";

const colorValue = [
    { color: "default", value: 1 },
    { color: "secondary", value: 2 },
    { color: "primary", value: 3 },
    { color: "success", value: 4 },
    { color: "info", value: 5 },
    { color: "warning", value: 6 },
    { color: "error", value: 7 },
];

function getHighestValueColor(colors) {
    const matchingColors = colorValue.filter((item) => colors.includes(item.color));

    if (matchingColors.length === 0) {
        return "default";
    }

    let maxColor = matchingColors[0];
    for (let i = 1; i < matchingColors.length; i++) {
        if (matchingColors[i].value > maxColor.value) {
            maxColor = matchingColors[i];
        }
    }

    return maxColor.color;
}

function getChildrenBadges(children, badges = []) {
    children.forEach((item) => {
        if (item.badges) {
            badges.push(...item.badges);
        }
        if (item.children) {
            getChildrenBadges(item.children, badges);
        }
    });
    return badges;
}

function getBadgeProps(item) {
    let count = 0;
    const colors = [];

    if (item.badges) {
        item.badges.forEach((badge) => {
            count++;
            colors.push(badge.color);
        });
    }

    if (item.children) {
        const childrenBadges = getChildrenBadges(item.children);

        childrenBadges.forEach((badge) => {
            count++;
            colors.push(badge.color);
        });
    }

    return {
        color: getHighestValueColor(colors),
        badgeContent: count,
        variant: "dot",
        anchorOrigin: {
            vertical: "top",
            horizontal: "left",
        },
    };
}

function ListIcon(props) {
    const {
        item, itemClick,
    } = props;

    const [disableConditions, setDisableConditions] = useState([]);

    const darkMode = useStore((store) => store.app.darkMode);

    const [menuProps, toggleMenu] = useMenuState();

    const menuRef = useRef(null);

    // Check if a list element should be hidden according to its ptm_config.json disable conditions
    const itemListDisabled = (listItem) => (
        disableConditions.some((condition) => listItem.disableConditions?.includes(condition))
    );

    const openMenu = (e, funcItem) => {
        if (funcItem.children && funcItem.children.length > 0) {
            toggleMenu(true);
        } else {
            itemClick(e, funcItem);
        }
    };
    const listIconMenuItem = (funcItem) => (
        !itemListDisabled(funcItem) && funcItem.children && funcItem.children.length > 0
            ? (
                <SubMenu
                    key={funcItem.id}
                    label={(
                        <>
                            <FontAwesomeIcon icon={funcItem.icon} fixedWidth />
                            {funcItem.display}
                        </>
                    )}
                >
                    {funcItem.children.map((child) => listIconMenuItem(child))}
                </SubMenu>
            )
            : !itemListDisabled(funcItem) && (
                <MenuItem
                    key={funcItem.id}
                    onClick={(e) => itemClick(e, funcItem)}
                >
                    <Badge
                        {...getBadgeProps(funcItem)}
                    >
                        <FontAwesomeIcon icon={funcItem.icon} fixedWidth />
                        {funcItem.display}
                    </Badge>
                </MenuItem>
            )
    );

    useEffect(() => {
        // Add disable conditions to disableConditions component state. These conditions will be matched
        // with the disableConditions property of the item to determine if the element should be hidden or not.
        const conditions = [];
        // eslint-disable-next-line no-undef
        if (_isBrowser) {
            conditions.push("isBrowser");
        }
        setDisableConditions(conditions);
    }, []);

    return (
        <>
            <PTMTooltip
                title={item.display}
            >
                <ListItem
                    ref={menuRef}
                    key={item.id}
                    style={{ backgroundColor: item.backgroundColor }}
                    onClick={(e) => openMenu(e, item)}
                    aria-haspopup="true"
                >
                    <ListItemIcon>
                        <Badge {...getBadgeProps(item)}>
                            {item.icon
                                ? <FontAwesomeIcon icon={item.icon} fixedWidth />
                                : <img src={item.image} alt="Über PT.Manager" style={{ width: 20 }} />}
                        </Badge>
                    </ListItemIcon>
                </ListItem>
            </PTMTooltip>
            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <ControlledMenu
                anchorRef={menuRef}
                {...menuProps}
                onClose={() => toggleMenu(false)}
                portal
                direction="right"
                theming={darkMode ? "dark" : null}
                style={{ zIndex: 1100 }}
            >
                <MenuHeader>
                    <FontAwesomeIcon icon={item.icon} fixedWidth />
                    {" "}
                    {item.display}
                </MenuHeader>
                {item.children ? item.children.map((child) => listIconMenuItem(child)) : null}
            </ControlledMenu>
        </>
    );
}

const StyledMenuList = styled("div", {
    shouldForwardProp: (prop) => !["isBottom"].includes(prop),
})(({ isBottom }) => ({
    ...isBottom ? {
        marginTop: "auto",
    } : {
        display: "contents",
    },
    ".list": {
        height: "100%",
        "& li": {
            display: "block",
            textAlign: "center",
            paddingTop: 2,
            paddingBottom: 2,
        },
        "& div": {
            minWidth: "initial",
            cursor: "pointer",
        },
        "& svg": {
            fontSize: 20,
        },
    },
}));

function MenuList(props) {
    const {
        entries: navigation,
        itemClick,
        isBottom,
    } = props;

    console.log(navigation);

    return (
        <StyledMenuList isBottom={isBottom}>
            <List className="list">
                {navigation.map((item) => (
                    <ListIcon item={item} key={item.id} itemClick={itemClick} />
                ))}
            </List>
        </StyledMenuList>
    );
}

function IconMenu(props) {
    const { menu, bottomMenu, contextAction } = props;
    console.log(bottomMenu);
    const setCurrentRoute = useStore((store) => store.app.setCurrentRoute);
    const logout = useStore((store) => store.app.logout);

    const dashboardAddTab = useStore((store) => store.dashboard.addTab);
    const dashboardOpenDialog = useStore((store) => store.dashboard.openDialog);
    const setCurrentTab = useStore((store) => store.dashboard.setCurrentTab);

    const setAddWidgetDialogOpen = useStore((store) => store.dashboard.widgets.setAddWidgetDialogOpen);
    const setNotificationsOpen = useStore((store) => store.notifications.setOpen);
    const setDashboardSelectionOpen = useStore((store) => store.dashboard.setDashboardSelectionOpen);

    const openDialog = useStore((store) => store.dashboard.dialogs.setCurrentOpenDialog);

    const menuClick = async (e, item) => {
        if (item.tab) {
            if (item.dialog) {
                dashboardOpenDialog({
                    icon: item.tabIcon ? item.tabIcon : item.icon,
                    title: item.tabDisplay ? item.tabDisplay : item.display,
                    plugin: !item.isCore && item.module,
                    component: item.tab,
                    data: item.data ? item.data : null,
                });
            } else {
                dashboardAddTab({
                    icon: item.tabIcon ? item.tabIcon : item.icon,
                    title: item.tabDisplay ? item.tabDisplay : item.display,
                    plugin: !item.isCore && item.module,
                    component: item.tab,
                    data: item.data ? item.data : null,
                }, item.newTab);
            }
        } else if (!item.action) {
            setCurrentRoute({
                route: item.href,
                title: item.tabDisplay ? item.tabDisplay : item.display,
                icon: item.icon,
            });
            setCurrentTab(-1);

            window.dispatchEvent(new CustomEvent("reactToAngular", {
                detail: {
                    action: "openRoute",
                    value: {
                        route: item.href,
                    },
                },
            }));
        } else if (item.action === "currentTab0") {
            setCurrentTab(0);
        } else if (item.action === "openAbout") {
            openDialog("about");
            // window.dispatchEvent(new CustomEvent("reactToAngular", {
            //     detail: {
            //         action: "openModal",
            //         value: {
            //             animation: false,
            //             templateUrl: "about.html",
            //             controller: ["$scope", ($scope) => {
            //                 // eslint-disable-next-line no-undef
            //                 $scope.appInfo = _appInfo;
            //             }],
            //             resolve: {
            //                 // appInfo: $scope.appInfo,
            //             },
            //         },
            //     },
            // }));
        } else if (item.action === "logout") {
            logout();
        } else if (item.action === "addWidget") {
            window.dispatchEvent(new CustomEvent("reactToAngular", {
                detail: {
                    action: "openRoute",
                    value: {
                        route: "/",
                    },
                },
            }));
            setAddWidgetDialogOpen(true);
        } else if (item.action === "dashboardSelection") {
            setCurrentTab(0);
            setDashboardSelectionOpen(true);
        } else if (item.action === "openNotifications") {
            setNotificationsOpen(true);
        } else if (item.action === "export") {
            // eslint-disable-next-line prefer-template
            await import("../../../Plugins/" + item.module[0].toUpperCase() + item.module.slice(1).toLowerCase() + "/Exports").then((module) => {
                // eslint-disable-next-line no-unused-expressions
                module.default[item.function]();
            }).catch(() => {});
        }
    };

    return (
        <>
            <MenuList entries={menu} itemClick={menuClick} contextAction={contextAction} />
            <MenuList isBottom entries={bottomMenu} itemClick={menuClick} contextAction={contextAction} />
        </>
    );
}

export default IconMenu;
